// Blood-draw scheduling — exact time, availability window, or all-day.
//
// One source of truth for: building the DB payload from the picker, prefilling
// the picker from a row, and formatting a schedule for display. Shared by the
// OutcomeDrawer picker, the admin submission detail, and the phleb draw views.
//
// Storage model (see src/database/add_availability_window.sql):
//   scheduling_type: 'exact' | 'window' | 'all_day'
//   exact   → blood_collection_time holds the appointment datetime
//   window  → availability_start_date..availability_end_date, optional
//             availability_start_time / availability_end_time (applied each day)
//   all_day → availability_start_date == availability_end_date, no times
//   blood_collection_time is ALWAYS set to the window start so existing
//   route/SLA/"due today" logic (which keys off it) keeps working.

// ── datetime-local <input> helpers ─────────────────────────────────────────
// "YYYY-MM-DDTHH:mm" in local time ⇄ ISO string.
export const toLocalDateTimeInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const fromLocalDateTimeInput = (val) => {
  if (!val) return null;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Combine a date input ("YYYY-MM-DD") + optional time input ("HH:mm") into an
// ISO string, interpreted in the user's local timezone. Defaults missing time
// to 09:00 so an all-day/anytime anchor lands in the morning rather than midnight.
const combineToIso = (dateStr, timeStr, fallbackTime = '09:00') => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T${timeStr || fallbackTime}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Empty picker state.
export const emptyScheduleState = {
  type: 'exact',
  exact: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
};

// ── Picker state ⇄ DB ──────────────────────────────────────────────────────

// Build the picker's working state from a submissions (or status-event) row.
export function scheduleStateFromRow(row) {
  if (!row) return { ...emptyScheduleState };
  const type = row.scheduling_type || 'exact';
  if ((type === 'window' || type === 'all_day') && row.availability_start_date) {
    return {
      type,
      exact: toLocalDateTimeInput(row.blood_collection_time),
      startDate: row.availability_start_date || '',
      endDate: row.availability_end_date || row.availability_start_date || '',
      startTime: (row.availability_start_time || '').slice(0, 5),
      endTime: (row.availability_end_time || '').slice(0, 5),
    };
  }
  return {
    ...emptyScheduleState,
    type: 'exact',
    exact: toLocalDateTimeInput(row.blood_collection_time),
  };
}

// Turn the picker state into the DB fields to write. Returns null when nothing
// usable was entered (so the caller can skip touching the schedule entirely).
// Always returns every column (nulling the unused ones) so switching modes
// clears the previous mode's data.
export function buildSchedulePayload(state) {
  if (!state) return null;
  const blank = {
    scheduling_type: 'exact',
    blood_collection_time: null,
    availability_start_date: null,
    availability_end_date: null,
    availability_start_time: null,
    availability_end_time: null,
  };

  if (state.type === 'window' || state.type === 'all_day') {
    if (!state.startDate) return null;
    const start = state.startDate;
    // all_day is a single date; window may span days (end defaults to start,
    // and is clamped so it never precedes start).
    let end = state.type === 'all_day' ? start : (state.endDate || start);
    if (end < start) end = start;
    const startTime = state.type === 'all_day' ? '' : (state.startTime || '');
    const endTime = state.type === 'all_day' ? '' : (state.endTime || '');
    return {
      ...blank,
      scheduling_type: state.type,
      availability_start_date: start,
      availability_end_date: end,
      availability_start_time: startTime || null,
      availability_end_time: endTime || null,
      blood_collection_time: combineToIso(start, startTime),
    };
  }

  // exact
  const iso = fromLocalDateTimeInput(state.exact);
  if (!iso) return null;
  return { ...blank, scheduling_type: 'exact', blood_collection_time: iso };
}

// ── Display formatting ─────────────────────────────────────────────────────

const fmtDateOnly = (dateStr) => {
  if (!dateStr) return '';
  // Parse as local noon to avoid timezone day-shift on date-only strings.
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtTimeOnly = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const d = new Date();
  d.setHours(Number(h), Number(m || 0), 0, 0);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const fmtExact = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() > 2099 || d.getFullYear() < 1900) return '';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

// Human-readable schedule for a submissions/status-event row.
// Returns '' when there's no schedule (callers can fall back to 'N/A').
export function formatSchedule(row) {
  if (!row) return '';
  const type = row.scheduling_type || (row.blood_collection_time ? 'exact' : null);
  if (!type) return '';

  if (type === 'window' || type === 'all_day') {
    const start = row.availability_start_date;
    if (!start) return fmtExact(row.blood_collection_time);
    const end = row.availability_end_date || start;
    const dateLabel = start === end ? fmtDateOnly(start) : `${fmtDateOnly(start)} – ${fmtDateOnly(end)}`;

    if (type === 'all_day') return `All day · ${dateLabel}`;

    const st = (row.availability_start_time || '').slice(0, 5);
    const et = (row.availability_end_time || '').slice(0, 5);
    let timeLabel = 'anytime';
    if (st && et) timeLabel = `${fmtTimeOnly(st)} – ${fmtTimeOnly(et)}`;
    else if (st) timeLabel = `from ${fmtTimeOnly(st)}`;
    else if (et) timeLabel = `by ${fmtTimeOnly(et)}`;
    const perDay = start === end ? '' : ' each day';
    return `${dateLabel} · ${timeLabel}${perDay}`;
  }

  return fmtExact(row.blood_collection_time);
}

// Short label for the scheduling type (badge text).
export function scheduleTypeLabel(type) {
  if (type === 'window') return 'Window';
  if (type === 'all_day') return 'All day';
  return 'Time';
}

// ── "is this draw scheduled on day X?" ──────────────────────────────────────
// A local YYYY-MM-DD for a date-only string or a timestamp. Date-only strings
// pass through untouched so a stored availability_start_date isn't shifted by
// timezone; timestamps convert in local time.
function localYmd(value) {
  if (!value) return null;
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const dt = new Date(raw);
  if (isNaN(dt.getTime())) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}

// The [start, end] local-date range a draw is scheduled across: a single day
// for exact/all-day, or the availability window. null when nothing is scheduled.
export function scheduledRange(row) {
  const type = row.scheduling_type || (row.availability_start_date ? 'window' : (row.blood_collection_time ? 'exact' : null));
  if ((type === 'window' || type === 'all_day') && row.availability_start_date) {
    const start = localYmd(row.availability_start_date);
    const end = localYmd(row.availability_end_date || row.availability_start_date) || start;
    if (start) return { start, end };
  }
  const day = localYmd(row.blood_collection_time);
  if (day) return { start: day, end: day };
  if (row.availability_start_date) {
    const start = localYmd(row.availability_start_date);
    const end = localYmd(row.availability_end_date || row.availability_start_date) || start;
    if (start) return { start, end };
  }
  return null;
}

// True when `ymd` (YYYY-MM-DD) falls within the draw's scheduled range —
// inclusive of both ends, so a multi-day window matches every day it spans.
export function scheduledOnDate(row, ymd) {
  const r = scheduledRange(row);
  return !!r && r.start <= ymd && ymd <= r.end;
}
